import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@apollo/client'
import { toast } from 'sonner'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import { Loader2, Save } from 'lucide-react'
import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Form, FormControl, FormItem, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { useLanguage } from '@/context/LanguageContext'
import { seedProfiles } from '@/lib/profileUtils'
import { mapLinkTargetFromRaw } from '@/lib/linkTarget'
import { DraggableFieldArray, reorderFieldArray } from '@/components/shared/DraggableFieldArray'
import { FooterColumnEditor } from './components/FooterColumnEditor'
import { GET_FOOTER_SETTINGS, UPDATE_FOOTER_SETTINGS } from './footerSettingsQueries'
import { buildFooterSettingsFormSchema, buildFooterVariables } from './footerSettingsPayload'
import { FooterColumnFormValues, FooterSettingsFormValues } from './types'

function createBlankColumn(allLanguages: string[]): FooterColumnFormValues {
  return {
    _key: crypto.randomUUID(),
    profiles: seedProfiles(undefined, allLanguages, lang => ({ language: lang, heading: '' })),
    links: [],
  }
}

function columnTitle(column: FooterColumnFormValues, index: number, languageId: number): string {
  const heading =
    column.profiles[languageId]?.heading?.trim() ||
    column.profiles.find(p => p.heading?.trim())?.heading
  return heading?.trim() ? heading : `Column #${index + 1}`
}

const SOCIAL_FIELDS: { name: keyof FooterSettingsFormValues; label: string; placeholder: string }[] = [
  { name: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
  { name: 'twitter', label: 'Twitter / X', placeholder: 'https://x.com/...' },
  { name: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
  { name: 'pinterest', label: 'Pinterest', placeholder: 'https://pinterest.com/...' },
  { name: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/...' },
]

export default function FooterSettings() {
  const { allLanguages, languageId } = useLanguage()
  const activeLanguageId = Math.max(0, languageId)

  const { data, loading } = useQuery(GET_FOOTER_SETTINGS)
  const [updateFooterSettings, { loading: saving }] = useMutation(UPDATE_FOOTER_SETTINGS, {
    refetchQueries: ['GET_FOOTER_SETTINGS'],
    onError: () => toast.error('Failed to save footer settings'),
  })

  // Recomputed whenever the active language changes so the schema's "needs
  // a heading" issue attaches to the ONE profile index the form actually
  // renders a field for — see `buildNavigationSettingsFormSchema`'s doc
  // (same reasoning). react-hook-form re-reads `resolver` off its own
  // options on every render, so a fresh resolver here takes effect
  // immediately.
  const resolver = useMemo(
    () => zodResolver(buildFooterSettingsFormSchema(activeLanguageId)),
    [activeLanguageId]
  )

  const form = useForm<FooterSettingsFormValues>({
    resolver,
    mode: 'onSubmit',
    defaultValues: {
      columns: [],
      footer_address: '',
      footer_phone: '',
      footer_email: '',
      footer_copyright_text: '',
      facebook: '',
      twitter: '',
      instagram: '',
      pinterest: '',
      linkedin: '',
    },
  })

  // Gated on `allLanguages.length > 0` — see `NavigationSettings` for why.
  useEffect(() => {
    if (loading || allLanguages.length === 0) return

    const shopSetting = data?.findFirstShopSetting
    const rawColumns: any[] = shopSetting?.footer_columns ?? []
    const columns: FooterColumnFormValues[] = rawColumns.map(rawColumn => ({
      _key: crypto.randomUUID(),
      profiles: seedProfiles(rawColumn.footer_column_profiles, allLanguages, lang => ({
        language: lang,
        heading: '',
      })),
      links: (rawColumn.footer_links ?? []).map((rawLink: any) => ({
        _key: crypto.randomUUID(),
        ...mapLinkTargetFromRaw(rawLink),
        profiles: seedProfiles(rawLink.footer_link_profiles, allLanguages, lang => ({
          language: lang,
          label: '',
        })),
      })),
    }))

    form.reset({
      columns,
      footer_address: shopSetting?.footer_address ?? '',
      footer_phone: shopSetting?.footer_phone ?? '',
      footer_email: shopSetting?.footer_email ?? '',
      footer_copyright_text: shopSetting?.footer_copyright_text ?? '',
      facebook: shopSetting?.facebook ?? '',
      twitter: shopSetting?.twitter ?? '',
      instagram: shopSetting?.instagram ?? '',
      pinterest: shopSetting?.pinterest ?? '',
      linkedin: shopSetting?.linkedin ?? '',
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data, allLanguages.length])

  // ONE `DragDropContext` for the whole page — react-beautiful-dnd doesn't
  // support nesting contexts, so the columns list and every column's
  // independent links list are siblings under this single context, routed
  // by `droppableId`. A drop that lands on a DIFFERENT droppable than it
  // started on is ignored, which is what keeps links from being dragged
  // across columns.
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination || source.droppableId !== destination.droppableId) return

    if (source.droppableId === 'footer-columns') {
      const current = form.getValues('columns') ?? []
      form.setValue('columns', reorderFieldArray(current, source.index, destination.index), {
        shouldDirty: true,
      })
      return
    }

    const match = /^footer-links-(\d+)$/.exec(source.droppableId)
    if (match) {
      // A dynamically-built path can't be narrowed to RHF's literal path
      // union — same dynamic-path convention as `CompositeMembersEditor`'s
      // `as any` field names.
      const path = `columns.${match[1]}.links` as any
      const current = form.getValues(path) ?? []
      form.setValue(path, reorderFieldArray(current, source.index, destination.index), {
        shouldDirty: true,
      })
    }
  }

  const onSubmit = async (values: FooterSettingsFormValues) => {
    try {
      await updateFooterSettings({ variables: buildFooterVariables(values) })
      toast.success('Footer settings saved')
    } catch (error) {
      console.error('Save footer settings error:', error)
    }
  }

  // `form.handleSubmit(onSubmit)` silently no-ops on invalid input — see
  // `NavigationSettings`'s `onInvalid` for why a toast is required here.
  const onInvalid = () => {
    toast.error('Fix the highlighted footer columns/links before saving')
  }

  const showLoading = loading || allLanguages.length === 0

  if (showLoading) {
    return (
      <PageContent>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)}>
          <div className="flex justify-between items-center mb-8">
            <HeaderSection
              title="Footer"
              subtitle="Manage the B2B portal's footer columns, links, and contact info"
            />
            <Button type="submit" disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Save
            </Button>
          </div>

          <div className="max-w-[1000px] space-y-6">
            <Card className="p-6">
              <h3 className="text-base font-semibold mb-4">Columns</h3>
              <DragDropContext onDragEnd={handleDragEnd}>
                <DraggableFieldArray
                  form={form}
                  name="columns"
                  droppableId="footer-columns"
                  onAdd={() => createBlankColumn(allLanguages)}
                  itemTitle={(column, index) => columnTitle(column, index, activeLanguageId)}
                  renderItem={index => (
                    <FooterColumnEditor
                      form={form}
                      index={index}
                      languageId={activeLanguageId}
                      allLanguages={allLanguages}
                    />
                  )}
                  addLabel="Add column"
                  emptyText="No footer columns yet."
                />
              </DragDropContext>
            </Card>

            <Card className="p-6">
              <h3 className="text-base font-semibold mb-4">Contact &amp; social</h3>
              <div className="space-y-4">
                <InputFieldContainer
                  label="Address"
                  control={form.control}
                  name="footer_address"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Textarea rows={3} {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Phone"
                  control={form.control}
                  name="footer_phone"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input type="text" {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Email"
                  control={form.control}
                  name="footer_email"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input type="text" {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <InputFieldContainer
                  label="Copyright Text"
                  control={form.control}
                  name="footer_copyright_text"
                  render={({ field }) => (
                    <FormItem className="col-span-3">
                      <FormControl>
                        <Input type="text" {...field} value={field.value ?? ''} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {SOCIAL_FIELDS.map(({ name, label, placeholder }) => (
                  <InputFieldContainer
                    key={name}
                    label={label}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem className="col-span-3">
                        <FormControl>
                          <Input
                            type="text"
                            placeholder={placeholder}
                            {...field}
                            value={field.value ?? ''}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </Card>
          </div>
        </form>
      </Form>
    </PageContent>
  )
}
